/**
 * LCDP-14855 - see commit description for details
 */

import React, { useState, useEffect, useCallback } from 'react';
import Scrollbars from '@atomic-reactor/react-custom-scrollbars';

/**
 * @param props.id
 * @param props.label
 * @param props.name
 * @param props.defaultValue
 * @param props.options
 * @param props.onChange
 * @param props.inputRef
 */

const visuallyHiddenStyles: React.CSSProperties = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: '1px',
};

const DoubleColumnSelect = (props): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = React.useRef(null);
    const instanceIdRef = React.useRef(`dcs-${Math.random().toString(36).slice(2, 10)}`);
    const baseId = `${instanceIdRef.current}-${
        props.id || props.name || 'prescription-select'
    }`.replace(/[^a-zA-Z0-9_-]/g, '-');
    const listboxId = `${baseId}-listbox`;
    const instructionsId = `${baseId}-instructions`;
    const selectedValueId = `${baseId}-value`;
    const accessibleLabelledBy = props['aria-labelledby'];

    const toggleSelect = () => {
        setIsOpen(prev => !prev);
    };

    const getOptionElements = useCallback(() => {
        if (!selectRef.current) return [];

        return Array.from(
            selectRef.current.querySelectorAll('.cl-prescription-select-table-option')
        ) as HTMLElement[];
    }, []);

    const focusOption = useCallback(
        (direction: 'up' | 'down', fromClosed = false) => {
            const options = getOptionElements();
            if (!options.length) return;

            const directionStep = direction === 'down' ? 1 : -1;
            const activeElement = document.activeElement as HTMLElement | null;
            let optionIndex = activeElement ? options.indexOf(activeElement) : -1;

            if (optionIndex === -1) {
                const selectedIndex = options.findIndex(
                    option => option.getAttribute('aria-selected') === 'true'
                );

                if (selectedIndex !== -1) {
                    optionIndex = fromClosed
                        ? selectedIndex
                        : Math.min(options.length - 1, Math.max(0, selectedIndex + directionStep));
                } else {
                    optionIndex = direction === 'down' ? 0 : options.length - 1;
                }
            } else {
                optionIndex =
                    direction === 'down'
                        ? Math.min(optionIndex + 1, options.length - 1)
                        : Math.max(optionIndex - 1, 0);
            }

            options[optionIndex]?.focus();
        },
        [getOptionElements]
    );

    const closeAndFocusTrigger = useCallback(() => {
        setIsOpen(false);
        if (selectRef.current) {
            selectRef.current.focus();
        }
    }, []);

    const handleKeyDown = event => {
        if (event.key === 'Tab') {
            setIsOpen(false);
            return;
        }

        if (event.key == 'Enter') {
            event.preventDefault();
            toggleSelect();
            return;
        }

        if (event.key == 'Escape') {
            event.preventDefault();
            setIsOpen(false);
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();

            const direction = event.key === 'ArrowDown' ? 'down' : 'up';
            if (!isOpen) {
                setIsOpen(true);
                setTimeout(() => {
                    focusOption(direction, true);
                }, 0);
                return;
            }

            focusOption(direction);
        }
    };

    const handleExitClick = event => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleExitClick, true);

        return () => {
            document.removeEventListener('click', handleExitClick, true);
        };
    }, []);

    useEffect(() => {
        if (props.reset && props.onChange) {
            props.onChange(props.defaultValue);
        }
    }, [props.reset]);

    const onChange = useCallback(
        value => {
            setIsOpen(false);
            if (props.onChange) props.onChange(formatValue(value));
        },
        [props.onChange]
    );

    const onKeyPress = useCallback(
        (event, value) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                setIsOpen(false);
                if (props.onChange) {
                    props.onChange(formatValue(value));
                }
                if (selectRef.current) {
                    selectRef.current.focus();
                }
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                closeAndFocusTrigger();
                return;
            }

            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                event.preventDefault();
                event.stopPropagation();

                const currentOption = event.currentTarget as HTMLElement;
                const currentColumn = currentOption.dataset.optionColumn;
                const rowIndex = currentOption.dataset.optionRowindex;
                if (!currentColumn || rowIndex === undefined) {
                    return;
                }

                const moveRight = event.key === 'ArrowRight';
                const targetColumn =
                    currentColumn === 'negative' && moveRight
                        ? 'positive'
                        : currentColumn === 'positive' && !moveRight
                        ? 'negative'
                        : '';

                if (!targetColumn || !selectRef.current) {
                    return;
                }

                const targetOption = selectRef.current.querySelector(
                    `.cl-prescription-select-table-option[data-option-column="${targetColumn}"][data-option-rowindex="${rowIndex}"]`
                ) as HTMLElement | null;

                if (targetOption) {
                    targetOption.focus();
                }
                return;
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                focusOption(event.key === 'ArrowDown' ? 'down' : 'up');
            }
        },
        [closeAndFocusTrigger, focusOption, props.onChange]
    );

    const renderOptions = (options, selectedValue) => {
        const renderOption = (option, className, optionColumn?, optionRowIndex?) => {
            const optionValue =
                option.props.value > 0 && !option.props.children.startsWith('+')
                    ? `+${option.props.children}`
                    : option.props.children;
            const optionId = `${listboxId}-option-${String(option.props.value).replace(
                /[^a-zA-Z0-9_-]/g,
                '-'
            )}`;
            const isSelected = selectedValue === formatValue(Number(option.props.value));

            return (
                <div
                    id={optionId}
                    key={option.props.value}
                    className={className}
                    onClick={() => onChange(option.props.value)}
                    onKeyDown={e => onKeyPress(e, option.props.value)}
                    tabIndex={-1}
                    role="option"
                    aria-selected={isSelected}
                    data-option-column={optionColumn}
                    data-option-rowindex={optionRowIndex}
                >
                    {optionValue}
                </div>
            );
        };

        const zeroOption = options.filter(option => {
            return option.props.value === 0 ? option : null;
        });
        const negativeOptions = options
            .filter(option => {
                return option.props.value < 0 ? option : null;
            })
            .sort((a, b) => {
                return a.props.value > b.props.value ? -1 : 1;
            });
        const positiveOptions = options
            .filter(option => {
                return option.props.value > 0 ? option : null;
            })
            .sort((a, b) => {
                return a.props.value < b.props.value ? -1 : 1;
            });

        return (
            <Scrollbars
                onClick={e => e.stopPropagation()}
                renderThumbVertical={props => (
                    <div {...props} className="cl-prescription-select-thumb"></div>
                )}
                renderTrackVertical={props => (
                    <div {...props} className="cl-prescription-select-track"></div>
                )}
                renderView={props => (
                    <div
                        {...props}
                        id={listboxId}
                        role="listbox"
                        aria-labelledby={accessibleLabelledBy}
                        className="cl-prescription-select-view"
                    />
                )}
            >
                {zeroOption.length > 0
                    ? renderOption(
                          zeroOption[0],
                          'cl-prescription-select-table-row cl-prescription-select-table-option'
                      )
                    : null}
                {negativeOptions.length > 0 && positiveOptions.length > 0 ? (
                    <div
                        className="cl-prescription-select-table-row scrollable-menu"
                        style={{ overflow: 'visible' }}
                    >
                        <div className="cl-prescription-select-table-col">
                            {negativeOptions.map((negativeOption, optionRowIndex) => {
                                return renderOption(
                                    negativeOption,
                                    'cl-prescription-select-table-row cl-prescription-select-table-option',
                                    'negative',
                                    optionRowIndex
                                );
                            })}
                        </div>
                        <div className="cl-prescription-select-table-col">
                            {positiveOptions.map((positiveOption, optionRowIndex) => {
                                return renderOption(
                                    positiveOption,
                                    'cl-prescription-select-table-row cl-prescription-select-table-option',
                                    'positive',
                                    optionRowIndex
                                );
                            })}
                        </div>
                    </div>
                ) : null}
            </Scrollbars>
        );
    };

    const fieldValue = () => {
        let value = '';

        if (props.value === undefined || props.value === null || props.value === '') {
            value = '';
        } else {
            value = formatValue(Number(props.value));
        }
        return value;
    };

    const formatValue = (val: number) => {
        const isIntegerValue = Number.isInteger(val);
        let formatVal = '';
        //0 has to be '+0.00'
        if (val >= 0) {
            formatVal = isIntegerValue ? `+${val}.00` : `+${val.toFixed(2)}`;
        } else {
            formatVal = isIntegerValue ? `${val}.00` : `${val.toFixed(2)}`;
        }

        return formatVal;
    };

    const currentValue = fieldValue();

    return (
        <div
            className={
                'PrescriptionSelect cl-prescription-select' +
                (props.disabled ? ' cl-prescription-select-disabled' : '') +
                (isOpen ? ' cl-prescription-select-open' : '')
            }
            onClick={props.disabled ? () => {} : toggleSelect}
            onKeyDown={props.disabled ? () => {} : handleKeyDown}
            ref={selectRef}
            tabIndex={props.disabled ? -1 : 0}
            role="combobox"
            aria-labelledby={accessibleLabelledBy}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-describedby={`${selectedValueId}`}
            aria-disabled={props.disabled}
        >
            <span id={selectedValueId} style={visuallyHiddenStyles}>
                {currentValue || 'No value selected'}
            </span>
            <input
                id={props.id}
                className="cl-prescription-select-value"
                value={currentValue}
                name={props.name}
                tabIndex={-1}
                readOnly
                ref={props.inputRef}
                {...props.register}
                aria-hidden="true"
            />
            <svg
                viewBox="0 0 27 24"
                fill="none"
                className="cl-prescription-select-arrow"
                aria-hidden="true"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.982 8.293a.928.928 0 000 1.414l7.82 7c.437.39 1.144.39 1.58 0l7.821-7a.928.928 0 000-1.414 1.212 1.212 0 00-1.58 0l-7.03 6.293-7.031-6.293a1.212 1.212 0 00-1.58 0z"
                ></path>
            </svg>
            {isOpen ? (
                <div className="cl-prescription-select-table">
                    {renderOptions(props.options, currentValue)}
                </div>
            ) : null}
        </div>
    );
};

export default DoubleColumnSelect;
